import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import Markdown from 'react-markdown';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { HelixStyles, HelixPalette, ICopy, Button, IUser, Avatar } from '@ideagen/helix-react-core';
import { useTranslation } from 'react-i18next';
import { HELIX_REACT_AI_TRANSLATION_NAMESPACE } from '../../locales';
import { FeedbackButtons } from './FeedbackButton';
import { ActionButton } from '../ActionButton';
import MazlanIcon from '../Animators/MazlanIcon';
import './MessageBox.scss';
import AgentQuestion from '../AgentQuestion/AgentQuestion';
import DynamicChart from '../Chart/Chart';
/**
 * A message box to enable the exchange of information between the user and the AI Agent.
 */
const MessageBox = ({ testId, dataId, style, messageInfo, containerWidth, ActionBar, isLoading = false, awaitingResponse = false }) => {
    // Hooks
    const { t } = useTranslation();
    // TODO: Implement message history pagination
    //const [currentPage, setCurrentPage] = useState(0);
    // Constants
    const { messageType, dateTime, userInfo, HistoryContent, statusCode } = messageInfo !== null && messageInfo !== void 0 ? messageInfo : {
        messageType: 'S',
        dateTime: null,
        userInfo: { isAgent: false, userName: '' },
    };
    const { isAgent, userName } = userInfo || {};
    const { ActionBarItems = [] } = ActionBar || {};
    const showActionBar = ActionBar === null || ActionBar === void 0 ? void 0 : ActionBar.showActionBar;
    const errorStyling = {
        backgroundColor: HelixPalette.red10,
        border: `2px solid ${HelixPalette.red70}`,
    };
    const userStyling = { backgroundColor: HelixPalette.neutral05 };
    const agentStyling = { backgroundColor: 'transparent' };
    const customStyling = messageType === 'E' ? errorStyling : isAgent ? agentStyling : userStyling;
    const displayName = userName && (userName === null || userName === void 0 ? void 0 : userName.trim().length) > 0 ? userName : undefined;
    //const totalHistoryCount = HistoryContent?.length ?? 0;
    const copyText = (contentBlocks) => {
        if (contentBlocks === undefined) {
            return;
        }
        const text = contentBlocks
            .filter(block => block.type === 'text')
            .map(block => block.content)
            .join('\n');
        navigator.clipboard
            .writeText(text)
            .catch((err) => {
            console.error('Failed to copy text: ', err);
        });
    };
    // Utility function to parse graph data
    const parseGraphData = (graphString) => {
        if (typeof graphString !== 'string' || graphString.length === 0)
            return null;
        try {
            const fixedObjString = graphString
                .replace(/'/g, '"') // Replace single quotes with double quotes first
                .replace(/&#39/g, "'") // Then replace HTML entity &#39; with single quotes
                .replace(/(\w+):\s*'([^']*)'/g, '"$1": "$2"') //Find values and wrap them in quotes
                .replace(/(\w+):/g, '"$1":'); //Find property names and wrap them in quotes
            return JSON.parse(fixedObjString);
        }
        catch (e) {
            console.error('Failed to parse graph data:', e);
            return null;
        }
    };
    //TODO: Implement message regeneration 
    /*   const onRegenerated = () => {
      setCurrentPage(totalHistoryCount + 2);
    }; */
    // const clickNext = () => {
    //   if (currentPage === totalHistoryCount + 1) {
    //     return;
    //   }
    //   setCurrentPage(currentPage + 1);
    // };
    // const clickPrev = () => {
    //   if (currentPage === 1) {
    //     return;
    //   }
    //   let currentSelected = currentPage - 1;
    //   if (currentSelected === undefined) {
    //     currentSelected = 0;
    //   }
    //   setCurrentPage(currentSelected);
    // };
    // useEffect(() => {
    //   if (currentPage === 0) {
    //     return;
    //   }
    //   if (currentPage !== totalHistoryCount + 1) {
    //     addClasstoDiv(dataId, 'content-div-0', 'hide');
    //   } else {
    //     removeClassfromDiv(dataId, 'content-div-0', 'hide');
    //   }
    //   const existingParentDiv = document.querySelectorAll(`[data-id^="${dataId}-history-content-value-"]`);
    //   existingParentDiv.forEach((parentDivs) => {
    //     if (!parentDivs.classList.contains('hide')) {
    //       parentDivs.classList.add('hide');
    //     }
    //   });
    //   setDivtoHide(dataId, `history-content-value-${currentPage}`, 'hide');
    //   //styling for disable on click on prev and next button
    //   if (currentPage === totalHistoryCount + 1) {
    //     addClasstoDiv(dataId, 'next-icon', 'disabled-onclick');
    //     setDivtoHide(dataId, 'prev-icon', 'disabled-onclick');
    //   } else if (currentPage === 1) {
    //     addClasstoDiv(dataId, 'prev-icon', 'disabled-onclick');
    //     setDivtoHide(dataId, 'next-icon', 'disabled-onclick');
    //   } else {
    //     setDivtoHide(dataId, 'next-icon', 'disabled-onclick');
    //     setDivtoHide(dataId, 'prev-icon', 'disabled-onclick');
    //   }
    // }, [currentPage, dataId, totalHistoryCount]);
    return (_jsx(Grid, { "data-testid": testId, "data-id": testId, container: true, padding: 0, maxWidth: 720, minWidth: 426, className: "helix-react-ai-message-box", children: _jsxs(Box, { width: containerWidth, minWidth: '352px', sx: Object.assign(Object.assign(Object.assign({}, customStyling), style), { padding: '8px', borderRadius: '8px' }), children: [_jsx(Stack, { direction: "row", justifyContent: "flex-start", alignItems: "center", spacing: 2, children: _jsxs(Grid, { container: true, children: [_jsx(Grid, { item: true, xs: 10, children: _jsxs(Stack, { direction: "row", justifyContent: "flex-start", alignItems: "center", spacing: isAgent ? 1.5 : 1, sx: {
                                        height: '32px',
                                    }, children: [isAgent ? (_jsx(MazlanIcon, { loading: isLoading, dataId: `${dataId}-mazlan-avatar` })) : (displayName ? (_jsx(Avatar, { size: 'sm', user: { name: displayName }, dataId: `${dataId}-icon-avatar`, showTooltip: false, style: { backgroundColor: HelixPalette.teal80, color: HelixPalette.white } })) : (_jsx("div", { className: "icon-avatar", "data-id": `${dataId}-icon-avatar`, children: _jsx(IUser, {}) }))), _jsx(Typography, Object.assign({}, HelixStyles.style_03, { "data-id": `${dataId}-display-name`, children: isAgent ? displayName : 'You' }))] }) }), _jsx(Grid, { item: true, xs: 2, children: _jsx(Stack, { direction: "row", justifyContent: "flex-end", alignItems: "center", spacing: 2, sx: {
                                        height: '24px',
                                    }, children: _jsx(Typography, Object.assign({}, HelixStyles.style_07, { "data-id": `${dataId}-created-datetime`, children: dateTime })) }) })] }) }, `${dataId}-item-title`), _jsxs(Grid, { container: true, children: [_jsx(Grid, { container: true, children: _jsx(Grid, { item: true, xs: 12, textAlign: 'left', className: 'content-block-container', children: messageType !== 'E' && (messageInfo === null || messageInfo === void 0 ? void 0 : messageInfo.contentBlocks) && messageInfo.contentBlocks.length > 0 ? (
                                // Render content blocks in order
                                messageInfo.contentBlocks.map((block) => {
                                    var _a;
                                    return (_jsx("div", { "data-id": `${dataId}-content-block-${block.id}`, className: `content-block`, children: block.type === 'text' ? (_jsx(Markdown, { children: block.content })) : block.type === 'action' ? (_jsx(ActionButton, { dataId: dataId, action: block.content, label: (_a = block === null || block === void 0 ? void 0 : block.label) !== null && _a !== void 0 ? _a : '' })) : block.type === 'input' ? (_jsx(AgentQuestion, { dataId: dataId, content: block.content, awaitingResponse: awaitingResponse })) : block.type === 'graph' ? ((() => {
                                            const parsedChartData = parseGraphData(block.content);
                                            return parsedChartData ? _jsx(DynamicChart, { config: parsedChartData }) : null;
                                        })()) : null }, `${dataId}-content-block-${block.id}`));
                                })) : messageType === 'E' && (messageInfo === null || messageInfo === void 0 ? void 0 : messageInfo.contentBlocks) && messageInfo.contentBlocks.length > 0 ? (_jsxs(_Fragment, { children: [_jsx(Typography, Object.assign({}, HelixStyles.style_06, { "data-id": `${dataId}-error-content`, sx: { color: HelixPalette.black }, children: messageInfo.contentBlocks.filter(block => block.type === 'text').map(block => block.content).join('') })), _jsxs(Typography, Object.assign({}, HelixStyles.style_06, { "data-id": `${dataId}-error-status`, sx: { color: HelixPalette.black }, children: [t('chatAgent.status', {
                                                    ns: HELIX_REACT_AI_TRANSLATION_NAMESPACE,
                                                }), ": ", statusCode] }))] })) : null }, `${dataId}-mesage-main`) }), HistoryContent && HistoryContent.length >= 1 ? (_jsx(Grid, { container: true, children: _jsx(Grid, { item: true, xs: 12, textAlign: 'left', children: HistoryContent.map((item, index) => (_jsx("div", { "data-id": `${dataId}-history-content-value-${index + 1}`, children: _jsxs(Typography, Object.assign({}, HelixStyles.style_06, { sx: { color: HelixPalette.black }, children: [item.content, ' '] })) }, `${dataId}-history-content-value-${index + 1}`))) }, `${dataId}-mesage-history`) })) : ('')] }), showActionBar ? (_jsx(Grid, { container: true, "data-id": "show-action-container", children: _jsx(Grid, { item: true, xs: 8, children: _jsxs(Stack, { "data-id": `${dataId}-action-bar`, direction: "row", justifyContent: "flex-start", alignItems: "center", spacing: 2, marginTop: 0.5, children: [_jsx(Button, { dataId: `${dataId}-copy-button`, leftIcon: _jsx(ICopy, {}), onClick: copyText.bind(this, messageInfo === null || messageInfo === void 0 ? void 0 : messageInfo.contentBlocks), style: {}, title: "Copy text", type: "universal", variant: "grey" }), messageInfo && _jsx(FeedbackButtons, { messageInfo: messageInfo, dataId: dataId }), ActionBarItems &&
                                    (ActionBarItems === null || ActionBarItems === void 0 ? void 0 : ActionBarItems.length) > 0 &&
                                    ActionBarItems.map((item, index) => (_jsx(React.Fragment, { children: item }, `${dataId}-action-${index}`)))] }, `${dataId}-action-bar`) }) })) : ('')] }) }));
};
export default MessageBox;
